<script setup lang="ts">
import { CfgButton, CfgTypography, CfgTypographySizes } from '@cfgtech/ui';
import type { CompanyId, Events, IssueId } from '@/composables/events/useEvents';
import { useEvents } from '@/composables/events/useEvents';
import { EntityEnum, UpdateTypeEnum } from '~/services/generated/client';
import { APP_SECTIONS_HASH } from '~/constants/hash';

const props = defineProps<{
  companyId: CompanyId | null,
  issueId: IssueId | null,
}>();

const route = useRoute();

const dayjs = useDayjs();

const { companyId, issueId } = toRefs(props);

const { events, leftEventsToLoad, isLoadMoreLoading, loadMoreEvents, fetchInitialEvents } = useEvents({
  companyId,
  issueId,
}, { lazy: true });

defineEmits<{
  'event:click': [],
  'load-more': []
}>();

await fetchInitialEvents();

const eventsWithPath = useArrayMap<Events, Events & { path: string | null }>(() => sortEventsByDate(events.value), (event) => {
  return {
    ...event,
    path: getEventPath(event)
  };
});

const showLoadMoreButton = computed(() => leftEventsToLoad.value > 0);
function sortEventsByDate(events: Events[]) {
  return events.sort((a, b) =>
    dayjs(a.data?.date || a.createdAt)
      .isBefore(dayjs(b.data?.date || b.createdAt))
      ? 1
      : -1);
}

function getEventPath (event: Events) {
  let isin = event.entity === EntityEnum.Issue ? event.entityId : event.companyIssues?.[0]?.isin;

  // Prevent change page on we are on the valid page;
  if (
    event.entity === EntityEnum.Company &&
    route.path.includes('portfolio') &&
    event.companyIssues?.find(({ isin }) => isin === route.params.id as string)
  ) {
    isin = route.params.id as string;
  }

  const portfolioBaseURL = `/portfolio/${isin}`;

  switch (event.type) {
    case UpdateTypeEnum.ReturnsNotPaid:
    case UpdateTypeEnum.ReturnsPaid:
    case UpdateTypeEnum.ReturnsPeriod:
      return '/returns';

    case UpdateTypeEnum.FinancialResultNew:
      return `${portfolioBaseURL}#${APP_SECTIONS_HASH.order.economicResults}`;

    case UpdateTypeEnum.CollateralAdded:
    case UpdateTypeEnum.CollateralRemoved:
      return `${portfolioBaseURL}#${APP_SECTIONS_HASH.order.collaterals}`;

    case UpdateTypeEnum.ChangeOwner:
    case UpdateTypeEnum.ChangeAddress:
      return `${portfolioBaseURL}#${APP_SECTIONS_HASH.order.about}`;

    case UpdateTypeEnum.DocumentAdded:
      return `${portfolioBaseURL}#${APP_SECTIONS_HASH.order.documents}`;

    case UpdateTypeEnum.ChangeIssueParameter:
      return `/${portfolioBaseURL}#${APP_SECTIONS_HASH.order.info}`;

    case UpdateTypeEnum.Invested25:
    case UpdateTypeEnum.Invested50:
    case UpdateTypeEnum.Invested75:
    case UpdateTypeEnum.Invested100:
      return portfolioBaseURL;

    case UpdateTypeEnum.LustrationDphEnd:
    case UpdateTypeEnum.LustrationDphStart:
    case UpdateTypeEnum.LustrationIsirStart:
    case UpdateTypeEnum.LustrationIsirEnd:
    case UpdateTypeEnum.LustrationLiquidationStart:
    case UpdateTypeEnum.LustrationLiquidationEnd:
      return `${portfolioBaseURL}#${APP_SECTIONS_HASH.order.warnings}`;

    case UpdateTypeEnum.News:
      return `${portfolioBaseURL}#${APP_SECTIONS_HASH.order.news}-${event.data.id}`;

    default:
      return null;
  }
};
</script>

<template>
  <div v-if="events.length" class="space-y-10">
    <ul class="space-y-4">
      <li v-for="(event, index) in eventsWithPath" :key="index">
        <EventsCard :event="event" @click="$emit('event:click')" />
      </li>
    </ul>

    <CfgButton
      v-if="showLoadMoreButton"
      class="w-full"
      :label="$t('events.loadMore.label')"
      :loading="isLoadMoreLoading"
      stroke
      @click="loadMoreEvents"
    />
  </div>

  <CfgTypography
    v-else
    class="flex items-center justify-center rounded-xl bg-brand/10 p-5"
    :size="CfgTypographySizes.md"
  >
    {{ $t('events.noEvents.title') }}
  </CfgTypography>
</template>
